/**
 * The Rozliczenia tab's wiring (T-C2, T-C8, T-B1b): the credit summary, the roster that names it,
 * the hours read that names the trainer's zone, one gate over all three, which client is open,
 * and the header's „Rozliczam żetonami" switch.
 *
 * The list is `model/creditLedger`'s; one client's history and acts are `useClientLedger`'s.
 *
 * ── The switch ─────────────────────────────────────────────────────────────────────────────────
 * On is sent at once; off asks first (T-B1b, mobile's own question), because while it is off every
 * balance leaves the trainer's view and the client's — frozen, not closed (R18). The PATCH shares
 * the Ustawienia tab's mutation scope, so a credits change and a settings change never race on the
 * wire. While it is out the switch shows the value it is sending; a refusal is said in the intro
 * line and the switch goes back to the server's value.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { assignableClients, rosterQueryOptions } from '~/entities/client';
import { patchBookingPolicy } from '~/shared/api/generated/endpoints/booking-policy/booking-policy';
import { ApiError } from '~/shared/api/mutator';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { creditSummaryQueryOptions, readBookingAgain } from '~/features/credit-ledger';

import { POLICY_WRITE, policyWritesSettled } from '../api/bookingQueries';
import { clientBalanceRows, type ClientBalanceRow } from '../model/creditLedger';
import { writeRefusal } from '../model/writeRefusal';
import { useClientLedgerReads } from './useClientLedger';
import { useScheduleRead } from './useScheduleRead';

export type LedgerTabContent =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'failed' }
  /** R18: the trainer runs no ledgers — T-C8. */
  | { readonly kind: 'off' }
  | {
      readonly kind: 'ready';
      readonly rows: readonly ClientBalanceRow[];
      /** When the trainer first switched credits on — the list's foot. */
      readonly since: string | null;
      /** The open client — the first row unless the trainer picked another. */
      readonly open: ClientBalanceRow | null;
    };

export interface LedgerTabState {
  readonly gate: SkeletonGate;
  readonly content: LedgerTabContent;
  readonly open: (customerId: number) => void;
  readonly retry: () => void;
  /** The switch's value as drawn: the one being sent while a PATCH is out. `null` before the read. */
  readonly creditsOn: boolean | null;
  readonly switching: boolean;
  readonly switchTo: (on: boolean) => void;
  /** Off is asked first. */
  readonly askingOff: boolean;
  readonly confirmOff: () => void;
  readonly keepOn: () => void;
  readonly switchRefusal: string | null;
  readonly announcement: string;
  readonly announce: (message: string) => void;
  readonly zone: string;
  readonly today: string;
  readonly nowMs: number;
}

export function useLedgerTab(initialClient: number | null): LedgerTabState {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const announcer = useAnnouncer();
  const summary = useQuery(creditSummaryQueryOptions());
  const roster = useQuery(rosterQueryOptions());
  const hours = useScheduleRead();
  const [picked, setPicked] = useState<number | null>(initialClient);
  // Whether the tab has been drawn: after that, another client's history is the card's own wait.
  const [entered, setEntered] = useState(false);
  const [askingOff, setAskingOff] = useState(false);
  const [switchRefusal, setSwitchRefusal] = useState<string | null>(null);

  const toggle = useMutation({
    ...POLICY_WRITE,
    mutationFn: (on: boolean) => patchBookingPolicy({ creditsEnabled: on }),
    onMutate: () => {
      setSwitchRefusal(null);
    },
    onSuccess: (_answer, on) => {
      // Switched off, the summary stands off at once: no frame shows the old value between the
      // answer and the read after it. Switched on, the read brings the balances back; the switch
      // holds the sent value until it has (the re-read is this mutation's own last step below).
      if (!on) {
        queryClient.setQueryData(creditSummaryQueryOptions().queryKey, (summary) =>
          summary === undefined ? summary : { ...summary, creditsEnabled: false },
        );
      }
      announcer.announce(
        t(on ? 'web:booking.credits.switchedOn' : 'web:booking.credits.switchedOff'),
      );
    },
    onError: (error) => {
      const refused = writeRefusal(
        error instanceof ApiError ? { status: error.status, code: error.code } : null,
      );
      setSwitchRefusal(`${t('web:booking.credits.switchFailed')} ${t(refused.key)}`);
    },
    // Returned, so the mutation stays pending until the re-read has landed and the switch never
    // flips back to the value it was sent away from.
    onSettled: () => (policyWritesSettled(queryClient) ? readBookingAgain(queryClient) : undefined),
  });

  const names = new Map(
    assignableClients(roster.data, i18n.language).map((client) => [
      client.customerId,
      client.label,
    ]),
  );

  const rows =
    summary.data === undefined ? [] : clientBalanceRows(summary.data, names, i18n.language);
  // The first client opens on arrival and STAYS open: an act that moves them down the list must not
  // swap the ledger under the trainer for whoever now tops it.
  const first = rows[0];
  if (picked === null && first !== undefined) setPicked(first.customerId);
  const open = rows.find((row) => row.customerId === picked) ?? first ?? null;
  // The client the tab opens on is read before the tab is drawn: both cards' heights are their
  // lists', so drawing the ledger card before its history landed grew it by that history (183px,
  // measured 27 September 2026). Read once the zone is known, so the keys are the card's own; the
  // widened schedule window is waited for too, or the card would mount on a key not yet in hand.
  const arrivingOn =
    entered || hours.gate.waiting || summary.data?.creditsEnabled !== true
      ? 0
      : (open?.customerId ?? 0);
  const arrival = useClientLedgerReads(arrivingOn, { zone: hours.zone, today: hours.today });
  const gate = useSkeletonGate(
    (summary.isPending && summary.errorUpdateCount === 0) ||
      (roster.isPending && roster.errorUpdateCount === 0) ||
      hours.gate.waiting ||
      (arrivingOn > 0 && (arrival.pending || arrival.schedule.isPlaceholderData)),
  );
  if (!entered && !gate.waiting) setEntered(true);
  // The dates on the list and in the history are on the trainer's clock: a failed hours read is a
  // failed tab, never a guess at the zone.
  const content: LedgerTabContent = gate.waiting
    ? { kind: 'waiting' }
    : // The list names clients from the roster: without it the rows would read „Klient", so a
      // failed roster is a failed tab with the retry, never nameless rows.
      summary.data === undefined || roster.data === undefined || hours.failed
      ? { kind: 'failed' }
      : !summary.data.creditsEnabled
        ? { kind: 'off' }
        : { kind: 'ready', rows, since: summary.data.creditsEnabledSince ?? null, open };

  const serverOn = summary.data?.creditsEnabled ?? null;

  return {
    gate,
    content,
    open: setPicked,
    retry: () => {
      if (summary.isError) void summary.refetch();
      if (roster.isError) void roster.refetch();
      hours.retry();
    },
    creditsOn: toggle.isPending ? toggle.variables : serverOn,
    switching: toggle.isPending,
    switchTo: (on) => {
      if (toggle.isPending) return;
      if (on) toggle.mutate(true);
      else setAskingOff(true);
    },
    askingOff,
    confirmOff: () => {
      setAskingOff(false);
      toggle.mutate(false);
    },
    keepOn: () => {
      setAskingOff(false);
    },
    switchRefusal,
    announcement: announcer.message,
    announce: announcer.announce,
    zone: hours.zone,
    today: hours.today,
    nowMs: hours.nowMs,
  };
}
